/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { combineLatest, Subject } from 'rxjs';
import { map, debounce, switchMap, filter, sample, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { Container, Rectangle, utils } from 'pixi.js-legacy';
import { TicketManager } from '../tickets/TicketManager';
import { SpriteTicket } from '../tickets/SpriteTicket';
import { SpriteMilestone } from '../tickets/SpriteMilestone';
import { TICKET_TEXTURE_OPTIONS } from '../tickets/TICKET_TEXTURE_OPTIONS';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { Ticket, Side, TicketRenderTypes } from '@touchplan/common';
import { drop } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { SpritePool } from 'src/app/app-data/models/data-types/GenericPool';
import { Camera } from '@appCanvasCommon/core/Camera';
import {
	LevelOfDetail,
	LEVEL_OF_DETAIL_SIGNALS,
	LEVEL_OF_DETAIL_TABLE,
} from '@appPlanApplication/lod/LevelOfDetail';
import { FancyTicketAction } from 'src/app/app-data/actions/ticket-actions';
import { TicketOverlap } from './TicketOverlap';
import { MILESTONE_SIZE_OFFSET2 } from '@appCanvasCommon/interfaces/milestone-size.interface';
import { OVERLAP_FORCE_DISABLED, OVERLAP_FORCE_DISABLED_OVERRIDE } from '@touchplan/strings';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import RBush from 'rbush';
import { TicketType } from '@touchplan/common';
import { UserPreferenceStateService } from '../../../services/user-preference-state.service';
import { TicketLayout } from '@appPlanApplication/tickets/TicketContainer';

enum TICKET_COLLISION_SETTINGS {
	FORCE_DISABLE_MAXIMUM_TICKETS = 1000,
}

/**
 * The TicketCollision is responsible for handling all collision checks. It has an observable collide$ which dispatches
 * an event to all subscribers when a collision takes place.
 *
 * It requires access to:
 *  - TicketManager : So it can access the visual representation of the Tickets (FancySprites)
 *
 *  A spatial hashing or QuadTree may need to be implemented to improve performance of axis-aligned bounding box
 *  collision detection (1,000 tickets checking against 1,000 tickets = 1,000,000 collision checks...).
 *  Alternatively, can also only do collision checks on tickets which are VISIBLE and within a certain Level of Detail level.
 */

export class TicketCollision {
	public dirty = false;
	/**
	 * Only do collision checks at LOW detail
	 */
	public lodLevel = LEVEL_OF_DETAIL_TABLE.VERY_HIGH;
	/**
	 * Used to control if ticket collisions are enabled or not.
	 */
	public enabled = false;
	public globalEnable = true;
	private _lastEnabled = false;
	private _deferredTickets;
	private _pool: SpritePool;
	/**
	 * Reference to the TicketManager. Used so we can access the SpriteTicket instance
	 */
	private _ticketManager: TicketManager;
	/**
	 * An instance of a FancyGraphics class. Used to draw custom shapes for our overlap mask
	 */
	private _graphics: FancyGraphics = new FancyGraphics();
	/**
	 * Thing that holds all of the ticket overlaps
	 */
	private _overlapContainer: Container = new Container();
	/**
	 * A list of things to destroy
	 */
	private _destroyList: any[] = [];
	/**
	 * Our QuadTree to organize objects in world space
	 */
	private rTree: RBush;

	private shutDownSubject$: Subject<boolean> = new Subject();
	private enableTicketOverlap$ = this.userPreferenceStateService.enableTicketOverlap$;
	/**
	 *
	 * @param {CanvasAngularLand} canvasAngularLand The canvasAngularLand object
	 * @param {TicketManager} ticketManager The ticketManager instance so we can access SpriteTicket's
	 * @param {Camera} camera The camera instance
	 * @param renderFn Thing that is called to update the view
	 */
	constructor(
		private canvasAngularLand: CanvasAngularLand,
		private userPreferenceStateService: UserPreferenceStateService,
		ticketManager: TicketManager,
		private camera: Camera,
		renderFn: () => any
	) {
		this._ticketManager = ticketManager;

		this._overlapContainer.addChild(this._graphics);

		this.rTree = new RBush();

		camera.cameraContainer.addChild(this._overlapContainer);

		const overlapCreator = (): TicketOverlap => {
			return new TicketOverlap(utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_TICKET_OVERLAP]);
		};

		const overlapPool: SpritePool = new SpritePool(overlapCreator, this._overlapContainer);

		this._destroyList.push(this._overlapContainer, overlapPool);

		// Combine the ticketManager update$ observable with the tickets.list$ observable.
		// Do this so once all tickets have been initialized, an update event is fired so all sub-systems can updae.
		const delay$ = this.canvasAngularLand.renderQueueWithCleanup$.pipe(drop(30));
		const combined$ = combineLatest([this.canvasAngularLand.tickets.list$, ticketManager.update$]).pipe(
			debounce(() => delay$),
			map((q) => q[0]),
			takeUntil(this.shutDownSubject$)
		);

		let onlyDisableOnceWhenPassThreshold = false;

		// Subscribe to the combined observable.
		// NOTE: This Observable will only fire after the events have settled for 30 frames
		combined$.subscribe((tickets: Map<string, Ticket>) => {
			this._deferredTickets = tickets;
			this._pool = overlapPool;
			if (!onlyDisableOnceWhenPassThreshold) {
				const result = tickets.size > TICKET_COLLISION_SETTINGS.FORCE_DISABLE_MAXIMUM_TICKETS;
				this.globalEnable = !result;
				if (result) {
					this.userPreferenceStateService.enableTicketOverlap = false;
				}
				onlyDisableOnceWhenPassThreshold = true;
			}
			if (!this.enabled || !this.globalEnable) {
				return;
			}
			this._check(tickets, overlapPool);

			renderFn();
		});

		// This observer only fires for selected tickets that are dragged.
		this.canvasAngularLand.tickets.selectedTicket$
			.pipe(
				switchMap((res) => {
					return this.canvasAngularLand.tickets.soakedRaw$.pipe(
						filter(
							(action) => res.list.has(action.key) && action.payload.renderType === TicketRenderTypes.drag
						)
					);
				}),
				sample(this.canvasAngularLand.renderQueueWithCleanup$),
				takeUntil(this.shutDownSubject$)
			)
			.subscribe((ticketAction: FancyTicketAction) => {
				if (!this.enabled || !this.globalEnable) {
					return;
				}
				this._dynamicSelfCheck(ticketAction.payload);
			});

		// Update overlaps once the camera stops moving
		const cameraDelay$ = this.canvasAngularLand.renderQueueWithCleanup$.pipe(drop(60));
		camera.changed$
			.pipe(
				debounce(() => cameraDelay$),
				takeUntil(this.shutDownSubject$)
			)
			.subscribe((newPos) => {
				if (!this.globalEnable) {
					this.clear();
					overlapPool.reset();
				} else if (this._deferredTickets) {
					this._check(this._deferredTickets, overlapPool);
					// Update the render
					renderFn();
				}
			});

		// Watch for changes to the setting storage
		this.enableTicketOverlap$.pipe(takeUntil(this.shutDownSubject$)).subscribe((enableTicketOverlap) => {
			this.globalEnable = enableTicketOverlap;
			if (!this.globalEnable) {
				this.clear();
				overlapPool.reset();
			} else if (this._deferredTickets) {
				this._check(this._deferredTickets, overlapPool);
			}

			if (
				this._deferredTickets &&
				this._deferredTickets.size > TICKET_COLLISION_SETTINGS.FORCE_DISABLE_MAXIMUM_TICKETS &&
				this.globalEnable
			) {
				this._logPerformanceWarning();
			}
		});

		LevelOfDetail.emitter.on(LEVEL_OF_DETAIL_SIGNALS.CHANGED, this.lodChanged);

		this.lodChanged(LevelOfDetail.getLODLevel(), LevelOfDetail.getLastLODLevel());
	}

	private clear(): void {
		this._graphics.clear();
	}

	private _logPerformanceWarning(): void {
		Logging.warning(OVERLAP_FORCE_DISABLED_OVERRIDE);
	}

	private _logOverlapDisabledNotice(): void {
		Logging.warning(OVERLAP_FORCE_DISABLED);
	}

	public update() {
		if (
			this.enabled &&
			this.globalEnable &&
			this.enabled !== this._lastEnabled &&
			this._deferredTickets &&
			this.dirty
		) {
			this._check(this._deferredTickets, this._pool);
		}
	}
	/**
	 * Turn ON/OFF the collision as detail level changes
	 * @param {number} newLod The new LOD value
	 * @param {number} oldLod The old LOD value
	 */
	protected lodChanged = (newLod: number, oldLod: number) => {
		this.enabled = this.lodLevel <= newLod;
		if (!this.enabled) {
			this._graphics.clear();
			if (this._pool) {
				this._pool.reset();
			}
			this._lastEnabled = this.enabled;
		} else if (this.enabled !== this._lastEnabled && this._deferredTickets && this._pool) {
			this.dirty = true;
		}
	};
	/**
	 * Applies 'live' collision detection for tickets being dragged, but only against tickets it previously collided against
	 * during the last collision check update.
	 * @param {Ticket} ticket The ticket data being updated
	 */
	private _dynamicSelfCheck(ticket: Ticket): void {
		const ticketSprite: SpriteTicket = this._ticketManager.get(ticket.$id);
		if (!ticketSprite) {
			return;
		}
		const container = ticketSprite.ticketContainer;
		if (
			(!ticketSprite.enableCollisions && !ticketSprite.visible) ||
			!ticketSprite.active ||
			!ticketSprite.renderable ||
			!ticketSprite.collidingWith.size ||
			!container ||
			!container.renderable
		) {
			return;
		}
		// Do a collision check against all tickets this ticket was/is currently colliding width
		// (Determined during the last collision check update)
		ticketSprite.collidingWith.forEach((previousTicket: SpriteTicket) => {
			const overlap: TicketOverlap = ticketSprite.collidingOverlaps.get(previousTicket);
			const collideResult: boolean = this._intersects(ticketSprite, previousTicket);
			// Only clear the overlap if there is NO collision
			if (!collideResult) {
				overlap.visible = false;
				// Delete the caches on both tickets
				ticketSprite.collidingWith.delete(previousTicket);
				ticketSprite.collidingOverlaps.delete(previousTicket);
				previousTicket.collidingWith.delete(ticketSprite);
				previousTicket.collidingOverlaps.delete(ticketSprite);
			} else {
				overlap.visible = false;
			}
		});
	}
	/**
	 * Performs a collision check on all tickets and determines which tickets are overlapping
	 * @param allTickets
	 * @param {SpritePool} overlapPool Pool of Overlap images
	 */
	private _check(allTickets: Map<string, Ticket>, overlapPool: SpritePool): void {
		// Clear the masks and the overlapPool
		// We check to make sure graphics exists first. Can cause an error by going back to dashboard then forward to the plan very quickly
		if (!this._graphics || !this.enabled || !this.globalEnable) {
			return;
		}
		this._graphics.clear();
		overlapPool.reset();

		let collisionChecks = 0;
		// window.perf.log("start collision check");
		allTickets.forEach((ticket: Ticket) => {
			const ticketSprite: SpriteTicket = this._ticketManager.get(ticket.$id);
			if (
				!ticketSprite ||
				!ticketSprite.enableCollisions ||
				!ticketSprite.visible ||
				!ticketSprite.active ||
				!ticketSprite.renderable
			) {
				return;
			}
			const container = ticketSprite.ticketContainer;
			if (!container || !container.visible || !container.renderable) {
				return;
			}
			const data = container.getData();
			const layout = container.getLayout();
			// Clear collidingWidth
			ticketSprite.collidingWith.clear();

			// MOC-4281 - QuadTree is grabbing close objects to local position...Get world positions
			const searchBoxWithSprite = {
				minX: layout.left,
				minY: layout.top,
				maxX: layout.left + layout.width,
				maxY: layout.top + layout.height,
				sprite: ticketSprite,
			};

			this.rTree.remove(searchBoxWithSprite);
			this.rTree.insert(searchBoxWithSprite);

			const closeObjects = this.rTree.search(searchBoxWithSprite);

			closeObjects.forEach((closeTicket: any) => {
				if (
					closeTicket.sprite === ticketSprite ||
					ticketSprite.collidingWith.get(closeTicket) ||
					closeTicket.sprite.collidingWith.get(ticketSprite)
				) {
					return;
				}
				const collideResult: boolean = this._intersects(ticketSprite, closeTicket.sprite);
				if (collideResult) {
					this._draw(ticketSprite, closeTicket.sprite, overlapPool);
				}
				collisionChecks++;
			});
		});
		// window.perf.log("end collision check", {previousEvent: "start collision check"});
		this._lastEnabled = this.enabled;
		this.dirty = false;
	}
	/**
	 * Draws the overlap ticket mask
	 * @param {SpriteTicket} ticket
	 */
	private _drawMasks(ticket: SpriteTicket): void {
		const ticketData: Ticket = ticket.getData();
		const ticketContainer = ticket.getTicketContainer();
		const ticketLayout = ticketContainer.getLayout();
		if (ticket.type === TicketType.Milestone) {
			const mTicket = ticket as SpriteMilestone;
			// const x = ticketData.view.left - MILESTONE_SIZE_OFFSET.WIDTH / 2;
			// const y = ticketData.view.top - MILESTONE_SIZE_OFFSET.HEIGHT / 2;
			const sideOffset =
				ticketData.view.side === Side.PULL
					? MILESTONE_SIZE_OFFSET2.PULL / 2
					: MILESTONE_SIZE_OFFSET2.ACTIVE / 2;
			const x = ticketLayout.left - sideOffset;
			const y = ticketLayout.top - sideOffset;
			this._graphics.lineStyle(1, 0x000000, 1);
			this._graphics.beginFill(0x000000);
			this._graphics.drawDiamond(
				x,
				y + mTicket.offsetSize.height / 2,
				mTicket.offsetSize.width,
				mTicket.offsetSize.height
			);
			this._graphics.endFill();
		} else if (ticket.type === TicketType.Task) {
			this._graphics.lineStyle(1, 0x000000, 1);
			this._graphics.beginFill(0x000000);
			this._graphics.drawRect(ticketLayout.left, ticketLayout.top, ticket.width, ticket.height);
			this._graphics.endFill();
		} else if (ticket.type === TicketType.Constraint) {
			this._graphics.lineStyle(1, 0x000000, 1);
			this._graphics.beginFill(0x000000);
			const r: number = ticketLayout.width / 2;
			// this._graphics.drawCircle(ticketData.view.left + (ticket.width / 2), ticketData.view.top + (ticket.height / 2) + offset, r);
			this._graphics.drawCircle(
				ticketLayout.left + ticketLayout.width / 2,
				ticketLayout.top + ticketLayout.height / 2,
				r
			);
			this._graphics.endFill();
		}
	}
	/**
	 * Draws the overlap
	 * @param {SpriteTicket} ticket
	 * @param {SpriteTicket} secondTicket
	 * @param {SpritePool} overlapPool
	 */
	private _draw(ticket: SpriteTicket, secondTicket: SpriteTicket, overlapPool: SpritePool): void {
		const intersection: Rectangle = this._getIntersectionRect(ticket, secondTicket);
		const overlapSprite: TicketOverlap = overlapPool.grab() as TicketOverlap;
		overlapSprite.x = intersection.x;
		overlapSprite.y = intersection.y;
		overlapSprite.width = intersection.width;
		overlapSprite.height = intersection.height;
		overlapSprite.renderable = true;

		this._drawMasks(ticket);

		overlapSprite.mask = this._graphics;

		// Set the cache on the ticket
		ticket.collidingWith.set(secondTicket, secondTicket);
		ticket.collidingOverlaps.set(secondTicket, overlapSprite);

		secondTicket.collidingWith.set(ticket, ticket);
		secondTicket.collidingOverlaps.set(ticket, overlapSprite);
	}
	/**
	 * Returns a rectangle of the intersection point
	 * @param {SpriteTicket} ticket
	 * @param {SpriteTicket} secondTicket
	 */
	private _getIntersectionRect(ticket: SpriteTicket, secondTicket: SpriteTicket): Rectangle {
		const firstTicketContainer = ticket.getTicketContainer();
		const secondTicketContainer = secondTicket.getTicketContainer();
		const firstLayout = firstTicketContainer.getLayout();
		const secondLayout = secondTicketContainer.getLayout();

		// const tickRec: Rectangle = new Rectangle(firstData.view.left, firstData.view.top, ticket.width, ticket.height);
		const tickRec: Rectangle = new Rectangle(
			firstLayout.left,
			firstLayout.top,
			firstLayout.width,
			firstLayout.height
		);
		// const secondTickRec: Rectangle = new Rectangle(secondData.view.left, secondData.view.top, secondTicket.width, secondTicket.height);
		const secondTickRec: Rectangle = new Rectangle(
			secondLayout.left,
			secondLayout.top,
			secondLayout.width,
			secondLayout.height
		);

		const x: number = Math.max(tickRec.x, secondTickRec.x);
		const width: number = Math.min(tickRec.x + tickRec.width, secondTickRec.x + secondTickRec.width);
		const height: number = Math.max(tickRec.y, secondTickRec.y);
		const y: number = Math.min(tickRec.y + tickRec.height, secondTickRec.y + secondTickRec.height);
		return new Rectangle(x, y, width - x, height - y);
	}
	/**
	 * Tests if two DisplayObjects are intersecting
	 * @param {PIXI.DisplayObject} a
	 * @param {PIXI.DisplayObject} b
	 */
	private _intersects(a: SpriteTicket, b: SpriteTicket): boolean {
		const ticketA: Ticket = a.getData();
		const ticketB: Ticket = b.getData();
		const ticketAContainer = a.getTicketContainer();
		const ticketBContainer = b.getTicketContainer();
		const ticketALayout = ticketAContainer.getLayout();
		const ticketBLayout = ticketBContainer.getLayout();
		if (!ticketA || !ticketA.view || !ticketB || !ticketB.view || !ticketALayout || !ticketBLayout) {
			return false;
		}
		return (
			ticketALayout.left + ticketALayout.width > ticketBLayout.left &&
			ticketALayout.left < ticketBLayout.left + ticketBLayout.width &&
			ticketALayout.top + ticketALayout.height > ticketBLayout.top &&
			ticketALayout.top < ticketBLayout.top + ticketBLayout.height
		);
	}
	/**
	 * Kill all the things
	 * @param options
	 */
	public destroy(options?) {
		this._destroyList.forEach((thing) => {
			thing.destroy(options);
		});

		this.rTree.clear();

		this.shutDownSubject$.next(true);
		this.rTree = null;
		this._graphics = null;
		this.canvasAngularLand = null;
		this._deferredTickets = null;

		LevelOfDetail.emitter.off(LEVEL_OF_DETAIL_SIGNALS.CHANGED, this.lodChanged);
	}
}
